// src/components/dialogs/BuoyDialog.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormHelperText,
  Box,
  Typography,
} from "@material-ui/core";
import Select from "react-select";
import { getGpsDevices } from "../../../api/gpsdevice"; // ajusta la ruta si es distinta

export default function BuoyDialog({ open, onClose, onSave, buoy }) {
  const [fullName, setFullName] = useState("");
  const [gpsDevices, setGpsDevices] = useState([]);
  const [gpsDevice, setGpsDevice] = useState(""); // guardamos solo el _id
  const [longitude, setLongitude] = useState("");
  const [latitude, setLatitude] = useState("");

  useEffect(() => {
    let mounted = true;
    getGpsDevices()
      .then((res) => {
        if (mounted && res?.status === 200) setGpsDevices(res.data || []);
      })
      .catch(() => {
        if (mounted) setGpsDevices([]);
      });

    if (buoy) {
      setFullName(buoy.fullName || "");
      setGpsDevice(
        typeof buoy.gpsDevice === "object"
          ? buoy.gpsDevice?._id || ""
          : buoy.gpsDevice || ""
      );
      setLongitude(buoy.longitude || "");
      setLatitude(buoy.latitude || "");
    } else {
      setFullName("");
      setGpsDevice("");
      setLongitude("");
      setLatitude("");
    }

    return () => {
      mounted = false;
    };
  }, [buoy]);

  const gpsOptions = useMemo(() => {
    const sorted = [...gpsDevices].sort((a, b) =>
      (a.code || "").localeCompare(b.code || "", "es", { sensitivity: "base" })
    );
    return sorted.map((d) => ({
      value: d._id,
      label: `${d.code}${d.phone ? ` (${d.phone})` : ""}`,
    }));
  }, [gpsDevices]);

  const selectedGps = useMemo(
    () => gpsOptions.find((o) => o.value === gpsDevice) || null,
    [gpsOptions, gpsDevice]
  );

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 1300 }),
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3f51b5" : base.borderColor,
      boxShadow: state.isFocused ? "0 0 0 1px #3f51b5" : base.boxShadow,
      "&:hover": { borderColor: "#3f51b5" },
    }),
  };

  const canSave = Boolean(fullName?.trim());

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{buoy ? "Edit Buoy" : "New Buoy"}</DialogTitle>
      <DialogContent>
        <TextField
          label="Full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          fullWidth
          margin="normal"
          required
        />

        <Box mt={2} mb={1}>
          <Typography variant="caption" color="textSecondary">
            GPS Device (optional)
          </Typography>
          <Select
            options={gpsOptions}
            value={selectedGps}
            onChange={(opt) => {
              if (opt) {
                setGpsDevice(opt.value);
                setLatitude("");
                setLongitude("");
              } else {
                setGpsDevice("");
              }
            }}
            placeholder="-- No GPS assigned --"
            isClearable
            menuPortalTarget={document.body}
            styles={selectStyles}
          />
          <FormHelperText>
            Puedes buscar por código o teléfono. Déjalo vacío si no quieres asignar GPS.
          </FormHelperText>
        </Box>
        <TextField
          label="Latitud (opcional)"
          value={latitude}
          onChange={(e) => {
            const val = e.target.value;
            setLatitude(val);
            if (val) { 
              setGpsDevice("");
            }
          }}
          fullWidth
          margin="normal"
          placeholder="p. ej., 41.40338"
        />

        <TextField
          label="Longitud (opcional)"
          value={longitude}
          onChange={(e) => {
            const val = e.target.value;
            setLongitude(val);
            if (val) {
              setGpsDevice("");
            }
          }}
          fullWidth
          margin="normal"
          placeholder="p. ej., 2.17403"
        />

        <FormHelperText>
          Déjalo vacío si has seleccionado un dispositivo GPS arriba.
        </FormHelperText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => {
            const lat = latitude?.trim();
            const lon = longitude?.trim();
            const latNum = lat !== "" && !isNaN(Number(lat)) ? Number(lat) : undefined;
            const lonNum = lon !== "" && !isNaN(Number(lon)) ? Number(lon) : undefined;

            onSave({
              ...buoy,
              fullName: fullName.trim(),
              gpsDevice: gpsDevice || null,
              latitude: gpsDevice ? null : (lat === "" ? null : latNum),
              longitude: gpsDevice ? null : (lon === "" ? null : lonNum),
            });
          }}
          className="btn btn-primary"
          variant="contained"
          disabled={!canSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
